import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchTerritories,
  updateTerritoryStores,
  type Territory,
} from "../../../../state/territories";
import {
  searchStores,
  fetchStoresByIds,
  type Store,
} from "../../../../state/stores";
import { fetchAllAgencies, type Agency } from "../../../../state/agencies";
import { useUsers, type User } from "../../../../state/users";
import { supabase } from "../../../../lib/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback, useEffect, useState } from "react";
import { Box } from "@radix-ui/themes";

const SEARCH_COLUMNS = ["name"];

export function ManageTerritories() {
  const { t } = useTranslation();
  const [users] = useUsers();
  const [agencies, setAgencies] = useState<Agency[]>([]);

  // Load all agencies for dropdown
  useEffect(() => {
    fetchAllAgencies().then(setAgencies);
  }, []);

  // Filter users to only show reps
  const reps = users.filter((u) => u.user_roles?.role === "rep");

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchTerritories(limit, offset, searchParams, sortParams),
    [],
  );

  const getUserName = (userId: string | null) => {
    if (!userId) return "—";
    const user = users.find((u) => u.id === userId);
    return user?.full_name ?? "—";
  };

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Territory | null,
  ) => {
    const territoryData = {
      name: data.name as string,
      agency_id: parseInt(data.agency_id as string, 10),
      user_id: data.user_id === "__empty__" ? null : (data.user_id as string),
    };

    const storeIds = (data.store_ids as number[]) ?? [];

    if (item) {
      // Update territory
      const { error } = await supabase
        .from("territories")
        .update(territoryData)
        .eq("id", item.id);
      if (error) throw new Error(error.message);

      // Update store composition
      await updateTerritoryStores(item.id, storeIds);
    } else {
      // Create territory
      const { data: newTerritory, error } = await supabase
        .from("territories")
        .insert([territoryData])
        .select("id")
        .single();
      if (error) throw new Error(error.message);

      // Link stores
      if (storeIds.length > 0) {
        await updateTerritoryStores(newTerritory.id, storeIds);
      }
    }
  };

  const handleDelete = async (item: Territory) => {
    // stores_territories rows will cascade-delete automatically
    const { error } = await supabase
      .from("territories")
      .delete()
      .eq("id", item.id);
    if (error) throw new Error(error.message);
  };

  return (
    <Box p="4">
      <Manager.Root<Territory>
        title={t("territories.title")}
        fetchData={fetchData}
      >
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("territories.searchPlaceholder")}
          />
          <Manager.SelectSearch<Agency>
            filterKey="agency_id"
            label={t("territories.agency")}
            options={agencies}
            getOptionLabel={(a) => a.name}
            getOptionValue={(a) => a.id}
            allLabel={t("territories.allAgencies")}
          />
          <Manager.SelectSearch<User>
            filterKey="user_id"
            label={t("territories.rep")}
            options={reps}
            getOptionLabel={(u) => u.full_name}
            getOptionValue={(u) => u.id}
            allLabel={t("territories.allReps")}
          />
        </Manager.Search>

        <Manager.CreateButton label={t("territories.createTerritory")} />

        <Manager.Table>
          <Manager.Column<Territory>
            header={t("territories.agency")}
            accessor={(terr) => terr.agencies?.name ?? "—"}
          />
          <Manager.Column<Territory>
            header={t("territories.name")}
            accessor={(terr) => terr.name}
          />
          <Manager.Column<Territory>
            header={t("territories.rep")}
            accessor={(terr) => getUserName(terr.user_id)}
          />
          <Manager.Column<Territory>
            header={t("territories.storeCount")}
            accessor={(terr) => terr.store_count ?? 0}
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.DeleteAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Territory>
          title={(isEdit) =>
            isEdit
              ? t("territories.editTerritory")
              : t("territories.createTerritory")
          }
          description={(isEdit) =>
            isEdit
              ? t("territories.editDescription")
              : t("territories.createDescription")
          }
          onSubmit={handleSubmit}
          validate={(data) => {
            const name = data.name as string;
            const agencyId = data.agency_id as string;
            return !!name?.trim() && !!agencyId && agencyId !== "__empty__";
          }}
        >
          <Manager.SelectField<Agency>
            name="agency_id"
            label={t("territories.agency")}
            options={agencies}
            getOptionLabel={(a) => a.name}
            getOptionValue={(a) => String(a.id)}
            getValue={(item) =>
              (item as Territory).agency_id
                ? String((item as Territory).agency_id)
                : ""
            }
            placeholder={t("territories.agencyPlaceholder")}
          />
          <Manager.TextField
            name="name"
            label={t("territories.name")}
            required
            placeholder={t("territories.namePlaceholder")}
            getValue={(item) => (item as Territory).name}
          />
          <Manager.SelectField<User>
            name="user_id"
            label={t("territories.rep")}
            options={reps}
            getOptionLabel={(u) => u.full_name}
            getOptionValue={(u) => u.id}
            getValue={(item) => (item as Territory).user_id}
            allowEmpty
            emptyLabel={t("common.none")}
            placeholder={t("territories.selectRep")}
          />
          <Manager.MultiAutocompleteField<Store>
            name="store_ids"
            label={t("territories.stores")}
            placeholder={t("territories.storesPlaceholder")}
            getValue={(item) => {
              const territory = item as Territory;
              return (
                territory.stores_territories?.map((st) => st.store_id) ?? []
              );
            }}
            searchFn={searchStores}
            fetchByIdsFn={fetchStoresByIds}
            getOptionLabel={(store) => store.name}
            getOptionValue={(store) => store.id}
          />
        </Manager.FormDialog>

        <Manager.DeleteDialog<Territory>
          getItemName={(terr) => terr.name}
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
